import { Contract, PriceList, Stake, Token } from '@jdiyield/interfaces'
import BigNumber from 'bignumber.js'
import Web3 from 'web3'

import BigFarmABI from '../abi/BigFarm.json'
import { calculate } from '../calculate'
import getNodeUrl from '../rpcRandom'
import { StakeFunc } from '../types'
import { balanceOf, findToken, safeGasEstimates } from '../utils'

const TREE = '0xdab0686d5f06deb4b721baa79da96e18cf19d029'
const LEAF = '0x1cbddf83de068464eba3a4e319bd3197a7eea12c'
const sdLEAF = '0xC51E41c88E1F7cCC12bc12748Bfd1CF3d0058908'
const sdBNB = '0xbb69e398c49A353D8359dEa4F750756566C9DFEa'
export const getSeederFarm = async (
  walletAddress: string,
  ct: Contract,
  tokens: Token[],
  priceList: PriceList,
  web3Prop: Web3
): Promise<Stake[]> => {
  try {
    const { contractAddress, rewardAddress, routerAddress } = ct
    const web3 = web3Prop || new Web3(getNodeUrl(56))
    const contract = new web3.eth.Contract(BigFarmABI as any, contractAddress)

    const allPools = (await contract.methods.getAllFarms().call()) as any[]
    const result = await Promise.all(
      allPools.map(async ({ stakeToken }, poolId) => {
        const farm = await contract.methods.getFarmer(poolId, walletAddress).call()
        const pendingReward = farm['totalPendingReward']
        const { stakeAmount } = farm['farmer']
        if (stakeAmount === '0') return null

        const availableBalanceOf = new BigNumber(await balanceOf(walletAddress, stakeToken)).dividedBy(1e18).toString()
        const lp = await calculate(
          web3,
          {
            pool: poolId,
            balance: stakeAmount,
            lpAddress: stakeToken,
            availableBalanceOf,
            rewardAddress
          },
          routerAddress,
          tokens,
          priceList
        )
        if (lp === null) return null
        let worth = 0
        const leafPrice = priceList[LEAF]?.usd ?? 0
        const BNBPrice = priceList['0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c']?.usd ?? 0
        switch (stakeToken.toLowerCase()) {
          case sdBNB.toLowerCase():
            worth = new BigNumber(stakeAmount)
              .dividedBy(`1e18`)
              .multipliedBy('1.0205')
              .multipliedBy(BNBPrice)
              .toNumber()
            break
          case sdLEAF.toLowerCase():
            worth = new BigNumber(stakeAmount)
              .dividedBy(`1e18`)
              .multipliedBy('1.0219')
              .multipliedBy(leafPrice)
              .toNumber()
            break
          case TREE.toLowerCase():
            worth = new BigNumber(stakeAmount).dividedBy(`1e18`).multipliedBy('1000').multipliedBy(leafPrice).toNumber()
            break
          default:
            worth = lp.worth
            break
        }

        const rewardInfo = findToken(tokens, rewardAddress)

        return {
          ...lp,
          ...ct,
          worth,
          availableBalanceOf,
          //TODO decimal always 18 ??
          balance: new BigNumber(stakeAmount).dividedBy(`1e18`).toString(),
          reward: new BigNumber(pendingReward).dividedBy(`1e${rewardInfo?.decimals || 18}`).toNumber(),
          rewardSymbol: rewardInfo?.symbol || '???'
        } as Stake
      })
    )
    return result.filter((obj) => obj !== null)
  } catch (error) {
    console.log(error)
    return []
  }
}

export const unStakeSeeder: StakeFunc = async ({ web3, pool, contractAddress, walletAddress, amount }) => {
  try {
    const contract = new web3.eth.Contract(BigFarmABI as any, contractAddress)
    const contractWithMethod = contract.methods.withdraw(
      pool,
      walletAddress,
      new BigNumber(amount).times(new BigNumber(10).pow(18)).toFixed()
    )
    const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

    await contractWithMethod.send({ from: walletAddress, gas: gasLimit })

    return true
  } catch (error) {
    console.log(error)
    return false
  }
}

export const stakeSeeder: StakeFunc = async ({ web3, pool, contractAddress, walletAddress, amount }) => {
  try {
    const contract = new web3.eth.Contract(BigFarmABI as any, contractAddress)
    const contractWithMethod = contract.methods.deposit(
      pool,
      walletAddress,
      new BigNumber(amount).times(new BigNumber(10).pow(18)).toFixed()
    )
    const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

    await contractWithMethod.send({ from: walletAddress, gas: gasLimit })

    return true
  } catch (error) {
    console.log(error)
    return false
  }
}
